/**
 * AiToolsDrafts — drafts inbox surface (Phase X.3.1 → X.3.4.5).
 *
 * X.3.1 stubbed the helper at `useAiDraftsStub() => []`. X.3.4.5 wires
 * through to the real `useAiDrafts(slug)` TanStack-Query hook backed by
 * GET /api/t/{slug}/v1/ai-tools/drafts. The page shape is unchanged —
 * the swap happens at the data-source seam per X.3.1's forward-compat
 * comment.
 *
 * Click-through to a draft detail page (/t/{slug}/ai-tools/drafts/{id})
 * is NOT in scope for X.3.4.5; X.3.5+ owns the reopen flow.
 *
 * X.3.4.12: a draft can be deleted straight from the list (no
 * click-through). The delete mutation lives here (the page owns
 * tenantSlug + the list query); it mirrors the per-draft delete on
 * AiToolsDraftDetail — same hard-delete endpoint, same list-query key
 * invalidation, same 403/404 copy. DraftsList stays a presenter and
 * renders the per-row control + inline confirm.
 */

import { type ReactNode, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTenant } from '@/lib/auth';
import { canUseAiTools, canViewAllDrafts } from '@/lib/capabilities';
import { aiToolsApi, ApiError } from '@/lib/api';
import { useAiDrafts } from '@/lib/aiDrafts';
import { contactSentence, pageWriteText } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';
import { AiToolsHeader } from '@/components/aiTools/AiToolsHeader';
import { EmptyAiToolsTile } from '@/components/aiTools/EmptyAiToolsTile';
import { PageShell } from '@/components/primitives/PageShell';
import { DraftsEmptyState } from '@/components/aiTools/DraftsEmptyState';
import { DraftsList } from '@/components/aiTools/DraftsList';

export function AiToolsDrafts(): ReactNode {
  const { whoami, tenantSlug } = useTenant();
  const queryClient = useQueryClient();
  // Hook called unconditionally per Rules of Hooks. `enabled` gate
  // suppresses the fetch when the user lacks the cap; the early-return
  // below still renders the empty-tile in that case.
  const capEligible = canUseAiTools(whoami);
  // X.3.4.11: owner-only "show all" toggle. Non-owners never see it and are
  // always scoped to own-only (the server re-derives this regardless).
  const canShowAll = canViewAllDrafts(whoami);
  const [scope, setScope] = useState<'own' | 'all'>('own');
  const effectiveScope = canShowAll ? scope : 'own';
  const { drafts, isLoading, isError } = useAiDrafts(tenantSlug, {
    enabled: capEligible,
    scope: effectiveScope,
  });
  const supportContact = useSupportContact({ enabled: capEligible });

  // X.3.4.12: hard-delete a draft from the list. Mirrors the per-draft
  // delete on AiToolsDraftDetail — same endpoint, same list-query-key
  // invalidation (the prefix clears both the own + all scope entries),
  // same 403/404 copy. The list re-fetches rather than splicing; after
  // the last row goes, the invalidated query yields [] -> empty state.
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const deleteMutation = useMutation({
    mutationFn: (id: string): Promise<void> => aiToolsApi.deleteDraft(tenantSlug, id),
    onMutate: () => setDeleteError(null),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['ai-tools', 'drafts', tenantSlug] });
    },
    onError: (e: unknown) => {
      if (e instanceof ApiError && e.status === 404) {
        setDeleteError("That draft is already gone. Reload the page to see what's left.");
      } else if (e instanceof ApiError && e.status === 403) {
        setDeleteError(
          contactSentence("You can't delete this draft. Ask the person who wrote it, or {contact_admin}.", supportContact),
        );
      } else {
        setDeleteError(pageWriteText(e, supportContact));
      }
    },
  });


  return (
    <PageShell maxWidth="xl" testId="ai-tools-drafts-main" access={{ allowed: capEligible, deniedContent: <EmptyAiToolsTile /> }}>
      <AiToolsHeader
        tenantSlug={tenantSlug}
        title="Drafts"
        rightLink={{ to: `/t/${tenantSlug}/ai-tools`, label: '← Writing' }}
      />
      {/* X.3.4.7 §6.1: warm Peace-palette section (warm-cream ground +
          sage left accent + forest heading), mirroring the email output
          blocks. DashboardSection is the shared white-card wrapper and
          can't be styled per-instance, so the drafts inbox renders its
          own styled section here. */}
      <section
        className="rounded-lg border-l-4 border-y border-r border-sage-800/[8%] p-4 space-y-3"
        style={{ backgroundColor: 'var(--color-cream)', borderLeftColor: 'var(--color-sage-400)' }}
        data-testid="drafts-section"
      >
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="text-lg font-semibold text-sage-800">
            {canShowAll && effectiveScope === 'all' ? 'All drafts' : 'Your drafts'}
          </h2>
          {/* UX-core pass 2026-07-06: the inline-style toggle (and its
              #C9D6CF drift hex) moves onto tokens + the house segmented
              geometry (rounded-base; sage-400 knob on paper). */}
          {canShowAll && (
            <div
              className="inline-flex overflow-hidden rounded-base border border-sage-200"
              role="group"
              aria-label="Drafts scope"
              data-testid="drafts-scope-toggle"
            >
              {(['own', 'all'] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setScope(s)}
                  aria-pressed={scope === s}
                  data-testid={`drafts-scope-${s}`}
                  className={
                    'min-h-9 px-3 py-1.5 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 ' +
                    (scope === s ? 'bg-sage-400 text-paper' : 'bg-paper text-sage-400 hover:text-sage-800')
                  }
                >
                  {s === 'own' ? 'My drafts' : 'All drafts'}
                </button>
              ))}
            </div>
          )}
        </div>
        {/* X.3.4.12: delete failures (403/404/other) surface here, above
            the list, reusing the detail-page copy. */}
        {deleteError !== null && (
          <p
            className="text-sm text-warm-700 bg-warm-50 border border-warm-200 rounded-lg px-3 py-2"
            role="alert"
            data-testid="drafts-delete-error"
          >
            {deleteError}
          </p>
        )}
        {isLoading ? (
          <p className="text-sm text-ink-soft" data-testid="drafts-loading">
            Loading drafts…
          </p>
        ) : isError ? (
          <p
            className="text-sm text-warm-700 bg-warm-50 border border-warm-200 rounded-lg px-3 py-2"
            role="alert"
            data-testid="drafts-error"
          >
            {contactSentence(
              "Your drafts didn't load. Reload the page, and if it happens again, tell {contact_system}.",
              supportContact,
            )}
          </p>
        ) : drafts.length === 0 ? (
          <DraftsEmptyState />
        ) : (
          <DraftsList
            drafts={drafts}
            tenantSlug={tenantSlug}
            onDelete={(id) => deleteMutation.mutate(id)}
            deletingId={deleteMutation.isPending ? deleteMutation.variables : null}
          />
        )}
      </section>
    </PageShell>
  );
}
